import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { PushFormClassPrefix as CLASS_PREFIX } from 'components/ExperienceCanvas/constants';
import { FormData, PushUniversalAction } from 'components/ExperienceCanvas/types';
import { Content } from 'interface/content/content.interface';
import ActionLinkFieldControl from '../ActionLinkFieldControl';
import ContentIdDropdown from './ContentIdDropdown';
import useContentIdSelector from './useContentIdSelector';

const CONTENT_DETAILS_LABEL = 'Content details';
const CONTENT_ID_DROPDOWN_MAX_HEIGHT_VH = 0.5;
const CONTENT_ID_DROPDOWN_VIEWPORT_PADDING_PX = 8;

type ContentIdFieldRowProps = {
  fieldKey: string;
  fieldMetadata: PushUniversalAction['fields'][number];
  field: FormData[string];
  updateField: (key: string, value: string | null) => void;
  toggleError: (key: string) => void;
  isDropdownOpen: boolean;
  contentIdSelector: ReturnType<typeof useContentIdSelector>;
  onSelectContentId: (contentId: string, contentName?: string) => void;
  selectedContentDisplayName?: string;
  lastSelectedContentId?: string;
};

const ContentIdFieldRow = ({
  fieldKey,
  fieldMetadata,
  field,
  updateField,
  toggleError,
  isDropdownOpen,
  contentIdSelector,
  onSelectContentId,
  selectedContentDisplayName = '',
  lastSelectedContentId = '',
}: ContentIdFieldRowProps) => {
  const anchorRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isDropdownOpen) return;

    const menu = contentIdSelector.menuRef.current;
    const anchor = anchorRef.current;
    if (!menu || !anchor) return;

    const updateMaxHeight = () => {
      const availableAbove = anchor.getBoundingClientRect().top - CONTENT_ID_DROPDOWN_VIEWPORT_PADDING_PX;
      const viewportCap = window.innerHeight * CONTENT_ID_DROPDOWN_MAX_HEIGHT_VH;
      const maxHeight = Math.max(0, Math.min(viewportCap, availableAbove));

      menu.style.setProperty('--content-id-dropdown-max-height', `${maxHeight}px`);
    };

    updateMaxHeight();
    window.addEventListener('resize', updateMaxHeight);
    document.addEventListener('scroll', updateMaxHeight, true);

    return () => {
      window.removeEventListener('resize', updateMaxHeight);
      document.removeEventListener('scroll', updateMaxHeight, true);
      menu.style.removeProperty('--content-id-dropdown-max-height');
    };
  }, [isDropdownOpen, contentIdSelector.menuRef]);

  const selectedContentName = useMemo(() => {
    const contentId = field?.value?.trim() ?? '';
    if (!contentId) return '';

    if (contentId === lastSelectedContentId && selectedContentDisplayName) {
      return selectedContentDisplayName;
    }

    const matchedOption = contentIdSelector.contentInstances?.find(
      (option) => (option.payload as Content).id === contentId,
    );

    return matchedOption?.name || contentId;
  }, [contentIdSelector.contentInstances, field?.value, lastSelectedContentId, selectedContentDisplayName]);

  return (
    <div ref={anchorRef} className={`${CLASS_PREFIX}__content-id-field-anchor`}>
      {isDropdownOpen && (
        <ContentIdDropdown
          menuRef={contentIdSelector.menuRef}
          searchVal={contentIdSelector.searchVal}
          onSearchChange={contentIdSelector.setSearchVal}
          contentInstances={contentIdSelector.contentInstances}
          isLoading={contentIdSelector.isLoading}
          isFetchingNextPage={contentIdSelector.isFetchingNextPage}
          isError={contentIdSelector.isError}
          errorMessage={contentIdSelector.errorMessage}
          onSelectContentId={onSelectContentId}
          setLastOptionElement={contentIdSelector.setLastOptionElement}
        />
      )}
      <div className={`${CLASS_PREFIX}__row`}>
        <ActionLinkFieldControl
          fieldKey={fieldKey}
          fieldMetadata={fieldMetadata}
          field={field}
          updateField={updateField}
          toggleError={toggleError}
          displayValue={selectedContentName}
          label={CONTENT_DETAILS_LABEL}
        />
      </div>
    </div>
  );
};

export default ContentIdFieldRow;
